import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ThemeConfig } from 'src/assets/data/enum/themeConfig';
import { SAASBRANDNAME } from 'src/assets/data/enum/saasBrandName.enum';
import { FarmService } from './farm.service';
import { ChemicalTreatmentTime } from './../_models/chemical/ChemicalTreatmentTime';
import { ChemicalActivity } from './../_models/chemical/ChemicalActivity';
import { ChemicalDangerLabelMapping } from './../_models/chemical/ChemicalDangerLabelMapping';
import { ChemicalDangerLabel } from './../_models/chemical/ChemicalDangerLabel';
import { ChemicalComposition } from './../_models/chemical/ChemicalComposition';
import { ChemicalLabel } from './../_models/chemical/ChemicalLabel';
import { ChemicalAdversity } from './../_models/chemical/ChemicalAdversity';
import { ChemicalWarning } from './../_models/chemical/ChemicalWarning';
import { Chemical } from './../_models/chemical/Chemical';
import { environment } from '../../../environments/environment';
import { Product } from '../_models/product';

@Injectable()
export class ChemicalService {
  private productServiceUrl = environment.productServiceUrl;
  currentPreferredProductsCountry: string;

  constructor(
    private http: HttpClient,
    private farmService: FarmService
  ) {
    this.setInitPAC();
  }

  setInitPAC() {
    this.farmService.getPreferredProductsCountry().subscribe((pac) => {
      this.currentPreferredProductsCountry = pac;
    });
  }

  getChemicals(productId: number, registerNumber: string | number): Observable<Array<Chemical>> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicals?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicals?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalAdversities(
    registerNumber: number,
    productId: number,
    commodityId: number = null,
    adversityName = null
  ): Observable<Array<ChemicalAdversity>> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalAdversities?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '') +
            (adversityName != null ? '&name=' + adversityName : '')
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalAdversities?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '') +
            (adversityName != null ? '&name=' + adversityName : '')
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalWarnings(registerNumber: number, productId: number): Observable<ChemicalWarning[]> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalWarnings?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalWarnings?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalLabels(registerNumber: number, productId: number): Observable<ChemicalLabel> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalLabels?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalLabels?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalCompositions(
    registerNumber: string | number,
    productId: number,
    commodityId: number = null
  ): Observable<ChemicalComposition[]> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalCompositions?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '')
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalCompositions?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '')
        )
        .pipe(map((response) => response));
    }
  }

  searchByCrop(commodityId?: string, adversityName?: string): Observable<Product[]> {
    const country = this.currentPreferredProductsCountry || 'ITA';
    const xSaas = ThemeConfig.brand || SAASBRANDNAME.XFARM;
    // Set the SaaS brand to receive the correct products from the backend
    const headers = new HttpHeaders({ 'x-saas': xSaas.toLocaleLowerCase() });

    let params = new HttpParams().set('preferredProductsCountry', country);
    if (commodityId) {
      params = params.set('commodityId', commodityId);
    }
    if (adversityName) {
      params = params.set('adversityName', adversityName);
    }

    const url = `${this.productServiceUrl}api/private/v1/lists/products`;

    return this.http.get<Product[]>(url, { headers, params });
  }

  getChemicalAdversityNames(
    productId: number,
    registerNumber: string,
    name: string,
    commodityId: number
  ): Observable<any> {
    let pathurl =
      this.productServiceUrl +
      'api/private/v1/lists/providers/chemicalAdversityNames?preferredProductsCountry=' +
      this.currentPreferredProductsCountry;

    if (productId) {
      pathurl += '&productId=' + productId;
    }
    if (name) {
      pathurl += '&name=' + name;
    }
    if (commodityId) {
      pathurl += '&commodityId=' + commodityId;
    }

    return this.http.get<any>(pathurl).pipe(map((response) => response));
  }

  getChemicalDangerLabels(
    registerNumber: number,
    productId: number
  ): Observable<ChemicalDangerLabel> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalDangerLabels?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalDangerLabels?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalDangerLabelMappings(registerNumber: number): Observable<ChemicalDangerLabelMapping> {
    return this.http
      .get<any>(
        this.productServiceUrl +
          'api/private/v1/lists/providers/chemicalDangerLabelMappings?registerNumber=' +
          registerNumber
      )
      .pipe(map((response) => response));
  }

  getChemicalActivities(registerNumber: number, productId: number): Observable<ChemicalActivity> {
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalActivities?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalActivities?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalTreatmentTimes(
    registerNumber: number,
    productId: number,
    commodityId: number = null,
    season: string = null
  ): Observable<ChemicalTreatmentTime[]> {
    let seasonParam = '';
    if (season) {
      seasonParam = '&variant=' + season;
    }
    if (productId) {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalTreatmentTimes?registerNumber=' +
            registerNumber +
            '&productId=' +
            productId +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '') +
            seasonParam
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .get<any>(
          this.productServiceUrl +
            'api/private/v1/lists/providers/chemicalTreatmentTimes?registerNumber=' +
            registerNumber +
            '&preferredProductsCountry=' +
            this.currentPreferredProductsCountry +
            (commodityId != null ? '&commodityId=' + commodityId : '') +
            seasonParam
        )
        .pipe(map((response) => response));
    }
  }

  getChemicalAdversityMappings(productId, commodityId = null) {
    return this.http
      .get<any>(
        this.productServiceUrl +
          'api/private/v1/lists/providers/chemicalAdversityMappings?productId=' +
          productId +
          '&preferredProductsCountry=' +
          this.currentPreferredProductsCountry +
          (commodityId != null ? '&commodityId=' + commodityId : '')
      )
      .pipe(map((response) => response));
  }
}
